"use client";

import React from "react";
import { Loader2, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { cn } from "@/lib/utils";
export default function ContactUs() {
  return <section data-section-id="39005" id="quote-request-form" className="relative bg-background text-foreground overflow-hidden min-h-screen flex items-center justify-center py-20">
      {}
      <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] bg-accent/10 rounded-full blur-3xl pointer-events-none opacity-60" />

      <div className="container relative z-10 mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h2 className="font-display font-bold text-secondary mb-4 tracking-tight !text-4xl">
            Start Your Transformation
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto font-sans">
            Tell us about your renovation vision. Our expert team is ready to bring your
            dream space to life with precision and style.
          </p>
        </div>

        <Card className="text-card-foreground flex flex-col gap-6 py-6 w-full max-w-5xl mx-auto bg-primary-light shadow-strong rounded-lg border-none overflow-hidden mb-10">
          <CardContent className="p-8 md:p-12">
            <FormProvider formKey="quote_request_form_getaquote" formId="quote_request_form_getaquote" sectionName="quote-request-form" defaultValues={{
            name: "",
            email: "",
            phone: "",
            relation: "",
            message: ""
          }} validationRules={{
            name: {
              required: "Name is required"
            },
            email: {
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email address"
              }
            },
            phone: {
              required: false
            },
            relation: {
              required: "Please select your project type"
            },
            message: {
              required: "Project details are required"
            }
          }} submitText="Request Quote" successMessage="Thank you! Your quote request has been received. Our design consultant will contact you shortly." errorMessage="Something went wrong. Please try again or call our studio.">
              <ContactFormContent />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="w-20 h-20 bg-accent/20 rounded-full flex items-center justify-center">
          <Send className="w-10 h-10 text-accent-dark" />
        </div>
        <h3 className="text-3xl font-display font-bold text-secondary">
          Request Received!
        </h3>
        <p className="text-muted-foreground text-lg max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-full px-8 border-accent text-accent hover:bg-accent hover:text-white transition-colors">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {}
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary font-semibold text-base">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="John Smith" {...field} className="h-12 border-border-strong bg-white focus-visible:border-accent focus-visible:ring-accent/20 focus-visible:ring-[3px] transition-all duration-300 rounded-md" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary font-semibold text-base">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+357 99 XX XX XX" {...field} className="h-12 border-border-strong bg-white focus-visible:border-accent focus-visible:ring-accent/20 focus-visible:ring-[3px] transition-all duration-300 rounded-md" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary font-semibold text-base">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="h-12 border-border-strong bg-white focus-visible:border-accent focus-visible:ring-accent/20 focus-visible:ring-[3px] transition-all duration-300 rounded-md" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="relation" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary font-semibold text-base">
                Project Category
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <select {...field} className={cn("flex h-12 w-full items-center justify-between rounded-md border border-border-strong bg-white px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-[3px] focus:ring-accent/20 focus:border-accent disabled:cursor-not-allowed disabled:opacity-50 appearance-none transition-all duration-300", !field.value && "text-muted-foreground")}>
                    <option value="" disabled>
                      Select a category
                    </option>
                    <option value="Full Residence Renovation">
                      Full Residence Renovation
                    </option>
                    <option value="Kitchen & Bath Remodel">
                      Kitchen & Bath Remodel
                    </option>
                    <option value="Interior Design Services">
                      Interior Design Services
                    </option>
                    <option value="Commercial Space Renovation">
                      Commercial Space Renovation
                    </option>
                    <option value="Other">Other</option>
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none opacity-50">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-secondary font-semibold text-base">
              Project Vision
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your dream renovation project and desired outcomes..." {...field} className="min-h-[160px] border-border-strong bg-white focus-visible:border-accent focus-visible:ring-accent/20 focus-visible:ring-[3px] transition-all duration-300 rounded-md resize-y" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-md animate-in fade-in slide-in-from-top-2">
          <p className="text-red-800 flex items-center gap-2">
            <span className="text-lg"></span> {submitError}
          </p>
        </div>}

      {}
      <div className="flex flex-col sm:flex-row items-center gap-4 pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto min-w-[220px] h-14 rounded-full bg-accent hover:bg-accent-hover text-foreground-accent text-lg font-bold shadow-medium hover:-translate-y-0.5 transition-all duration-300">
          {isSubmitting ? <>
              <Loader2 className="animate-spin mr-2 h-5 w-5" />
              Processing...
            </> : "Request Quote"}
        </Button>
      </div>
    </Form>;
}